import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { type InviteResponse } from '../../store/auth/auth'
import { RoleType } from '../../models/Role'
import { AssignSeatModal } from './AssignSeatModal'
import { BuyRequestedSeatsModal } from './BuyRequestedSeatsModal'
import { BuySeatModal } from './BuySeatModal'
import { RequestedSeatGroup } from './RequestedSeatGroup'
import { RequestedSeatsAlert } from './RequestedSeatsAlert'
import { useSeatPurchaseGate } from './seatPurchaseGate'
import { RevokeRequestModal } from './RevokeRequestModal'
import { TeamEmptyState } from './TeamEmptyState'
import { type RequestedGroupKey } from './requestedGroups'
import { useAssistantSeatsOffered } from './useAssistantSeatsOffered'
import { useRequestedSeatGroups } from './useRequestedSeatGroups'
import { useRequestedSeatShortage } from './useRequestedSeatShortage'

type ActiveModal =
  | { kind: 'assign'; invite: InviteResponse; seatType: RoleType; freeSeats: number }
  | { kind: 'buy'; invite: InviteResponse; seatType: RoleType }
  | { kind: 'revoke'; invite: InviteResponse }
  | { kind: 'buyAll' }
  | null

export interface RequestedSeatGroupsProps {
  pages: Record<RequestedGroupKey, number>
  perPage: number
  namePattern: string | null
  roleGroupNames: string[]
  buySeatFor?: string | null
  onBuySeatForHandled?: () => void
  onChangePage: (key: RequestedGroupKey, page: number) => void
  onChangePerPage: (perPage: number, page: number) => void
}

export const RequestedSeatGroups: FC<RequestedSeatGroupsProps> = ({
  pages,
  perPage,
  namePattern,
  roleGroupNames,
  buySeatFor,
  onBuySeatForHandled,
  onChangePage,
  onChangePerPage,
}) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const { groups, refreshSeats } = useRequestedSeatGroups({ pages, perPage, namePattern, roleGroupNames })
  const shortage = useRequestedSeatShortage()
  const canBuy = useSeatPurchaseGate()
  const { offered: assistantOffered, isLoading: offerLoading } = useAssistantSeatsOffered()
  const canBuySeatType = useCallback((seatType: RoleType) => canBuy && (seatType !== RoleType.SIDECAR || assistantOffered), [canBuy, assistantOffered])

  const buyFromLink = useMemo<ActiveModal>(() => {
    if (!buySeatFor) {
      return null
    }
    const match = groups
      .flatMap((group) => group.rows.map((groupRow) => ({ invite: groupRow.invite, seatType: group.seatType })))
      .find((candidate) => candidate.invite.id === buySeatFor)
    return match && canBuySeatType(match.seatType) ? { kind: 'buy', invite: match.invite, seatType: match.seatType } : null
  }, [buySeatFor, canBuySeatType, groups])

  useEffect(() => {
    if (!buySeatFor || buyFromLink) {
      return
    }
    if (!offerLoading && !groups.some((group) => group.isFetching)) {
      onBuySeatForHandled?.()
    }
  }, [buySeatFor, buyFromLink, groups, offerLoading, onBuySeatForHandled])

  const shownModal = activeModal ?? buyFromLink

  const closeModal = () => {
    if (buySeatFor) {
      onBuySeatForHandled?.()
    }
    setActiveModal(null)
  }
  const openAssignModal = useCallback(
    (invite: InviteResponse, seatType: RoleType, freeSeats: number) => setActiveModal({ kind: 'assign', invite, seatType, freeSeats }),
    [],
  )
  const openBuyModal = useCallback((invite: InviteResponse, seatType: RoleType) => setActiveModal({ kind: 'buy', invite, seatType }), [])
  const openRevokeModal = useCallback((invite: InviteResponse) => setActiveModal({ kind: 'revoke', invite }), [])
  const openBuyAllModal = useCallback(() => setActiveModal({ kind: 'buyAll' }), [])
  const changePerPage = useCallback((nextPerPage: number) => onChangePerPage(nextPerPage, 1), [onChangePerPage])

  const hasActiveFilters = namePattern !== null || roleGroupNames.length > 0
  const isFetching = groups.some((group) => group.isFetching)
  const showsEmptyState = !hasActiveFilters && groups.every((group) => group.totalRows === 0)

  return (
    <div>
      {shownModal?.kind === 'assign' ? (
        <AssignSeatModal
          invite={shownModal.invite}
          seatType={shownModal.seatType}
          freeSeats={shownModal.freeSeats}
          onHide={closeModal}
          onSeatsChanged={refreshSeats}
        />
      ) : null}
      {shownModal?.kind === 'buy' ? (
        <BuySeatModal invite={shownModal.invite} seatType={shownModal.seatType} onHide={closeModal} onSeatsChanged={refreshSeats} />
      ) : null}
      {shownModal?.kind === 'revoke' ? <RevokeRequestModal invite={shownModal.invite} onHide={closeModal} /> : null}
      {shownModal?.kind === 'buyAll' ? <BuyRequestedSeatsModal onHide={closeModal} onSeatsChanged={refreshSeats} /> : null}
      {shortage.canBuy ? <RequestedSeatsAlert types={shortage.types} onBuySeats={openBuyAllModal} /> : null}
      {isFetching ? (
        <div className='d-flex align-items-center justify-content-center p-4'>
          <Spinner />
        </div>
      ) : showsEmptyState ? (
        <TeamEmptyState chip='requested' />
      ) : (
        groups.map((group) => (
          <RequestedSeatGroup
            key={group.key}
            group={group}
            perPage={perPage}
            canBuy={canBuySeatType(group.seatType)}
            onAssignSeat={openAssignModal}
            onBuySeat={openBuyModal}
            onRevokeRequest={openRevokeModal}
            onChangePage={onChangePage}
            onChangeRowsPerPage={changePerPage}
          />
        ))
      )}
    </div>
  )
}
